/* [ START OF MODULE - VER 2.3 (src/utils/ticketUtils.js) ] */

export const STATUS_META = {
  // RMI-2026-593: `dot` is a CSS var() reference, same as `color`, on every
  // theme-adaptive entry. WoCard/WorkOrderHeader/WorkOrderPreviewPanel/
  // SpecialistSchedulePanel/the Schedule calendar (useCommandCenterData) all
  // tint a badge/border from meta.dot via CSS color-mix() (e.g.
  // `color-mix(in srgb, ${meta.dot} 9%, transparent)`), which works
  // correctly with a var() -- unlike the old hex-alpha string-concatenation
  // pattern (`${meta.dot}18`) this replaced, which silently produced invalid
  // CSS for any var()-based dot (RMI-2026-592 papered over that by hardcoding
  // ON_SITE/DELAYED/COMPLETED/INCOMPLETE to hex, losing theme-adaptiveness;
  // this properly fixes the consumers instead). EN_ROUTE/IN_PROGRESS still
  // use single hex values below because they were deliberately chosen as
  // one exact shade in both themes (see VER 1.5/1.6 history), not because
  // concatenation required it.
  EN_ROUTE:      { color: 'var(--accent-secondary)', dot: '#5899CD', label: 'EN ROUTE' },
  ON_SITE:       { color: 'var(--accent-primary)',   dot: 'var(--accent-primary)', label: 'ON SITE'  },
  IN_PROGRESS:   { color: '#06B6D4',                 dot: '#06B6D4', label: 'IN PROG'  },
  DELAYED:       { color: 'var(--accent-danger)',    dot: 'var(--accent-danger)', label: 'PAUSED'  }, // RMI-2026-435: display label only, status code stays DELAYED
  COMPLETED:     { color: 'var(--accent-success)',   dot: 'var(--accent-success)', label: 'DONE'     },
  INCOMPLETE:    { color: 'var(--accent-danger)',    dot: 'var(--accent-danger)', label: 'INCOMPLETE'},
  // RMI-2026-248: CANCELLED and VOIDED were never registered here, so
  // getMeta('CANCELLED') / getMeta('VOIDED') fell through to STATUS_META.PENDING
  // (label 'UNKNOWN') -- a voided WO rendered its status as UNKNOWN in the WO
  // details form (WorkOrderHeader) and the CC work order list (WoCard). Same
  // class as the INVOICED (RMI-2026-064) and PAID (BUILD-385) fallthroughs.
  // Hex literals (not theme vars) because WoCard/WorkOrderHeader concat alpha
  // onto meta.dot (a hex is required). Neutral slate/gray = terminal/dead,
  // distinct from the active ladder and from INCOMPLETE's danger-red.
  CANCELLED:     { color: '#94A3B8',                 dot: '#94A3B8', label: 'CANCELLED' },
  // RMI-2026-506: VOIDED's original #64748B (slate-500) was too dark/low-
  // saturation to read at the low-opacity badge fill every consumer applies
  // (meta.dot+18/44 hex alpha) -- the badge nearly disappeared against a dark
  // background. A first pass lightened it within the slate family (#7C8BA1)
  // but user follow-up confirmed that still read as "grey" -- moved out of
  // the neutral-terminal family entirely to a real red (matches
  // --accent-danger's dark-mode value), same hue INCOMPLETE/DELAYED already
  // use. The two statuses sharing a hue is acceptable here since VOIDED's
  // own label text always disambiguates it in the badge.
  VOIDED:        { color: '#EF4444',                 dot: '#EF4444', label: 'VOIDED'    },
  OPEN:          { color: 'var(--accent-highlight)', dot: '#F37664', label: 'OPEN'     },
  OFFERED:       { color: 'var(--accent-highlight)', dot: '#F37664', label: 'OFFERED'  },
  ACCEPTED:      { color: 'var(--accent-highlight)', dot: '#F37664', label: 'ACCEPTED' },
  // RMI-2026-064: INVOICED is a real status the cascade trigger writes when an
  // invoice is dispatched. It was incorrectly stripped from STATUS_META in May
  // (under the assumption billing statuses were retired) which caused getMeta
  // to fall through to PENDING/UNKNOWN for every billed WO. Gold = matches the
  // billing accent used elsewhere in the app (True Tiger gold).
  INVOICED:      { color: '#FFC107',                 dot: '#FFC107', label: 'INVOICED' },
  // BUILD-385: PAID is the final, collected state (added system-wide in BUILD-286)
  // but was never registered here, so getMeta('PAID') fell through to the PENDING
  // 'UNKNOWN' tripwire below. Emerald is distinct from COMPLETED's var(--accent-success) and
  // reads as 'collected / closed out'.
  PAID:          { color: '#059669',                 dot: '#059669', label: 'PAID'     },
  // PENDING is NOT a valid work_order status code -- it only lives in this map
  // as the fallback for unknown statuses. Label says UNKNOWN so any WO that
  // surfaces here is visually flagged as a data-integrity issue rather than
  // masquerading as a normal phase.
  PENDING:       { color: 'var(--accent-highlight)', dot: '#F37664', label: 'UNKNOWN'  },
};

export const getMeta = (s) => STATUS_META[(s || 'PENDING').toUpperCase()] || STATUS_META.PENDING;

export const toBucket = (s) => {
  const u = (s || '').toUpperCase();
  if (['OPEN','OFFERED','ACCEPTED','PENDING'].includes(u))          return 'OPEN';
  if (['EN_ROUTE','ON_SITE','DELAYED','IN_PROGRESS'].includes(u))   return 'IN_PROGRESS';
  // RMI-2026-064: INVOICED rolls up into the COMPLETED bucket -- field work is
  // done. Combined with RMI-056's date_paid-IS-NOT-NULL exclusion in the filter
  // chain, the Done filter button surfaces COMPLETED-or-INVOICED-but-not-paid
  // records (the live billing pipeline) and excludes records already collected.
  if (['COMPLETED','INVOICED','PAID'].includes(u))                  return 'COMPLETED';
  if (['INCOMPLETE','CANCELLED','VOIDED'].includes(u))              return 'CANCELLED';
  return 'OPEN';
};

// Partner-ingest barcode refs: B + digits + T + 1-2 digits (T1, T2, ... -- the suffix is the barcode label number,
// not part of the PO). RMI-2026-685: this used to match only T1, so a scan/search for B5939753T2 found nothing.
const PARTNER_BARCODE_REF = /^B(\d{6,10})T\d{1,2}$/i;

// For display, show only the digits (e.g. B5910291T1 -> 5910291, B5939753T2 -> 5939753).
export const displayCustomerRef = (ref) => {
  if (!ref) return '';
  const m = String(ref).trim().match(PARTNER_BARCODE_REF);
  return m ? m[1] : ref;
};

// Strip the B prefix and T# suffix from partner barcode search terms so the DB query matches the stored
// customer_ref (which is just the digits). "B5939753T2" -> "5939753"; "5910291" passes through unchanged.
export const normalizeSearchTerm = (term) => {
  if (!term) return term;
  const m = String(term).trim().match(PARTNER_BARCODE_REF);
  return m ? m[1] : term;
};

export const elapsed = (iso) => {
  if (!iso) return null;
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  return m < 60 ? `${m}m` : `${Math.floor(m/60)}h${m%60}m`;
};

export const calculateEndTime = (startTimeStr, durationHrs) => {
  if (!startTimeStr) return '';
  const [h, m] = startTimeStr.split(':').map(Number);
  const addMins = (parseFloat(durationHrs) || 1.0) * 60;
  const totalMins = (h * 60) + m + addMins;
  
  const endH = Math.floor(totalMins / 60) % 24;
  const endM = Math.floor(totalMins % 60);
  return `${String(endH).padStart(2, '0')}:${String(endM).padStart(2, '0')}`;
};

export const getDisplaySchedule = (wo) => {
  const durationHrs = parseFloat(wo.planned_duration_hours) || 1.0;
  
  if (wo.time_started_at) {
    const startD = new Date(wo.time_started_at);
    const startDateStr = startD.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
    const startTimeStr = `${String(startD.getHours()).padStart(2,'0')}:${String(startD.getMinutes()).padStart(2,'0')}`;
    
    let endTimeStr = '';
    if (wo.time_completed_at) {
       const endD = new Date(wo.time_completed_at);
       endTimeStr = `${String(endD.getHours()).padStart(2,'0')}:${String(endD.getMinutes()).padStart(2,'0')}`;
    } else {
       const endD = new Date(startD.getTime() + durationHrs * 60 * 60 * 1000);
       endTimeStr = `${String(endD.getHours()).padStart(2,'0')}:${String(endD.getMinutes()).padStart(2,'0')}`;
    }
    
    return { date: startDateStr, timeString: `${startTimeStr} - ${endTimeStr}`, isActual: true, startString: startTimeStr };
  } 
  
  if (wo.scheduled_date || wo.scheduled_time) {
    const dateStr = wo.scheduled_date ? new Date(wo.scheduled_date + 'T00:00:00').toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }) : '';
    const endT = calculateEndTime(wo.scheduled_time, durationHrs);
    const timeStr = wo.scheduled_time ? `${wo.scheduled_time.substring(0, 5)} - ${endT}` : '';
    return { date: dateStr, timeString: timeStr, isActual: false, startString: wo.scheduled_time ? wo.scheduled_time.substring(0, 5) : null };
  }
  
  return null;
};

// ── ACTOR NAME RESOLUTION ────────────────────────────────────────────────────
// Single authoritative source for resolving a display name for system_activity_logs.
// Priority: profiles_v2 first+last > user_metadata.full_name > email > fallback.
// Pass both profile (from AuthContext) and user (from supabase.auth.getUser());
// profile is preferred because full_name in user_metadata is only set for OAuth
// sign-ins — email/password users never have it, causing emails to surface in logs.
export const getActorName = (profile, user, fallback = 'Dispatcher') => {
  if (profile?.first_name || profile?.last_name) {
    return `${profile.first_name || ''} ${profile.last_name || ''}`.trim();
  }
  if (user?.user_metadata?.full_name?.trim()) return user.user_metadata.full_name.trim();
  if (user?.email) return user.email;
  return fallback;
};

// ARCHITECTURE FIX: Absolute chronological sorting bypasses alphabetical "missing zero" bugs
export const sortTickets = (ticketsArray) => {
  return [...ticketsArray].sort((a, b) => {
    // 1. Chronological Date Sort (fallback to year 9999 for unscheduled)
    const dA = new Date(a.scheduled_date || '9999-12-31').getTime();
    const dB = new Date(b.scheduled_date || '9999-12-31').getTime();
    
    // Safety catch for invalid strings (NaN)
    const safeA = isNaN(dA) ? 8640000000000000 : dA;
    const safeB = isNaN(dB) ? 8640000000000000 : dB;

    if (safeA !== safeB) return safeA - safeB; // Ascending order

    // 2. Time Tie-Breaker (fallback to end of day)
    const timeA = String(a.scheduled_time || '23:59:59');
    const timeB = String(b.scheduled_time || '23:59:59');
    if (timeA !== timeB) return timeA.localeCompare(timeB);

    // 3. ID Tie-Breaker (Highest/Newest first)
    const idA = String(a.display_id || a.id || '');
    const idB = String(b.display_id || b.id || '');
    return idB.localeCompare(idA); 
  });
};

/*
=========================================
CHANGE MANIFEST
=========================================
VER 2.3 — BUILD-2026.1061 — RMI-2026-685: barcode refs accept any T-suffix (B5939753T2 -> 5939753), not just T1
=========================================
VER 2.2 — BUILD-2026.736 — RMI-2026-435: DELAYED display label -> "Paused"
- STATUS_META.DELAYED.label changed from 'DELAYED' to 'PAUSED'. Presentation
  only -- the status CODE stored on work_orders.status and everywhere else
  in logic stays 'DELAYED' (no CHECK constraint or data change). Cascades to
  every getMeta()/STATUS_META consumer: WoCard, WorkOrderHeader,
  WorkOrderPreviewPanel, WoStateBadge, SiteWorkOrdersModal,
  SpecialistSchedulePanel. Part of a broader sweep (see docs/CHANGELOG.md)
  that also touched several component-local label copies and raw-status-code
  renders that don't go through this map.
=========================================
VER 1.9 — July 1, 2026 — BUILD-2026.484 — RMI-2026-248: VOIDED/CANCELLED rendered as UNKNOWN
- BUG: STATUS_META never registered VOIDED or CANCELLED. getMeta('VOIDED') and
  getMeta('CANCELLED') fell through to STATUS_META.PENDING (label 'UNKNOWN'),
  so a voided (or cancelled) WO showed status UNKNOWN in the WO details form
  (WorkOrderHeader badge) and the Command Center WO list card (WoCard badge).
  Same class as the INVOICED (RMI-2026-064) and PAID (BUILD-385) fallthroughs.
- FIX: added STATUS_META.CANCELLED (#94A3B8, label 'CANCELLED') and
  STATUS_META.VOIDED (#64748B, label 'VOIDED'). Hex literals because the badge
  bg/border in WoCard + WorkOrderHeader concat alpha onto meta.dot.
- toBucket UNCHANGED: it already handled VOIDED/CANCELLED (both bucket to
  CANCELLED), so voided WOs never leaked into the active list -- label-only fix.
=========================================
VER 1.8 — June 10, 2026 — BUILD-2026.403 — RMI-2026-201: Replaced hardcoded hex colors with CSS custom properties in STATUS_META (DELAYED/INCOMPLETE→accent-danger, COMPLETED→accent-success, ON_SITE dot→accent-primary)

VER 1.7 — June 7, 2026 — BUILD-2026.385 — PAID missing from STATUS_META/toBucket
- BUG: PAID (added system-wide in BUILD-286) was never registered in STATUS_META
  or toBucket. getMeta('PAID') fell through to STATUS_META.PENDING whose label is
  'UNKNOWN' (the data-integrity tripwire), and toBucket('PAID') fell through to
  the 'OPEN' default — so every PAID work order rendered as UNKNOWN and leaked
  into the Command Center ACTIVE list. Surfaced via SRQ-4RRKN-01.
- FIX: STATUS_META.PAID = emerald #059669, label 'PAID' (distinct from COMPLETED).
- FIX: toBucket maps PAID into the COMPLETED bucket (alongside COMPLETED/INVOICED).
- Gap left by the BUILD-286 audit sweep — the CC list views were not covered.
=========================================
VER 1.6 — June 1, 2026 — BUILD-2026.342 — RMI-2026-154 follow-up: IN_PROGRESS green → cyan
- The BUILD-341 green (#22C55E) collided conceptually with COMPLETED's green
  (var(--accent-success)) — two greens on the same board risk reading as the same state.
  Green also semantically means 'done/success', not 'active'.
- Changed IN_PROGRESS to cyan #06B6D4 (color + dot). Cyan reads as 'live /
  active', is distinct from every other status color, and works in both
  themes. Palette is now a clean cool-color progression: EN_ROUTE blue
  (#5899CD) → IN_PROGRESS cyan (#06B6D4) → COMPLETED green (var(--accent-success)).
- Distinct from the nearest neighbor, EN_ROUTE's #5899CD: cyan is markedly
  more teal/saturated than that muted slate-blue.
- Same single-hex constraint as before (WoCard alpha concat needs a hex).
  Same shared scope (all getMeta consumers). ON_SITE / MiniMap pin /
  BreadcrumbTrailModal still purple, unchanged.
=========================================
VER 1.5 — June 1, 2026 — BUILD-2026.341 — RMI-2026-154: IN_PROGRESS status color purple → green
- Faheem disliked the brand-purple used for the IN_PROGRESS status on the
  Command Center work order cards. Changed STATUS_META.IN_PROGRESS from
  { color: var(--accent-primary), dot: var(--accent-primary) } (purple) to a single green
  hex #22C55E for both color and dot.
- WHY A SINGLE HEX (not a theme var): WoCard builds the badge background and
  border by string-concatenating alpha onto the dot (`${meta.dot}18`,
  `${meta.dot}44`), which requires a hex literal — a CSS var would break it.
  COMPLETED already hardcodes a single green (var(--accent-success)) that works in both
  themes; this follows the same pattern. #22C55E reads in both light and dark.
- DISTINCT FROM COMPLETED: COMPLETED is the teal-emerald var(--accent-success); IN_PROGRESS
  is the grass-green #22C55E. Different enough that an in-progress card and a
  done card are not mistaken for each other (labels also differ: IN PROG vs
  DONE, and the in-progress duration shows a pulsing ▶ elapsed timer).
- SCOPE: STATUS_META is shared, so in-progress is now green everywhere getMeta
  is consumed — WoCard (the cards), WorkOrderHeader badge, WorkOrderPreviewPanel,
  WoStateBadge (SRQ), SpecialistSchedulePanel. Consistent by design.
- NOT CHANGED: ON_SITE (separate status, still var(--accent-primary)/var(--accent-primary)),
  the MiniMap in_progress pin (var(--accent-primary)), and BreadcrumbTrailModal (#8B5CF6).
  These use independent purple values and can be matched on request.
=========================================
VER 1.4 — May 20, 2026 — BUILD-2026.206 — RMI-2026-064: restore INVOICED to STATUS_META and toBucket
- BUGFIX: VER 1.3 (May 17) stripped INVOICED from STATUS_META and toBucket on
  the theory that billing statuses were retired. But cascade_invoice_status
  (DB trigger) writes work_orders.status='INVOICED' on every invoice dispatch.
  Result: getMeta('INVOICED') fell through to STATUS_META.PENDING for every
  billed WO (displayed label 'PENDING'), and toBucket('INVOICED') fell through
  to default 'OPEN' so they surfaced under the Pending filter button -- even
  though PENDING isn't a real wo status.
- ADDED STATUS_META.INVOICED: gold (#FFC107, True Tiger gold), label 'INVOICED'
- toBucket('INVOICED') now rolls up into the COMPLETED bucket. Field work IS
  done at that point. Combined with RMI-2026-056's date_paid IS NOT NULL
  filter exclusion (BUILD-202), the Done filter button now means
  'COMPLETED-or-INVOICED but not yet paid' -- the live billing pipeline.
- STATUS_META.PENDING label renamed to 'UNKNOWN'. PENDING is not a valid
  work_order status code (see tecc_work_order_statuses dictionary -- only
  OPEN through VOIDED). The entry only exists in this map as the unknown-
  status fallback. Anything that surfaces here is a data-integrity flag.
=========================================
VER 1.3 — May 17, 2026 — Retire billing statuses from STATUS_META and toBucket
- Removed READY TO BILL, INVOICED, CLOSED from STATUS_META (retired statuses)
- Added INCOMPLETE to STATUS_META
- toBucket: removed retired statuses, INCOMPLETE/CANCELLED/VOIDED now map to CANCELLED bucket
- Removed BLOCK markers per code style rules
VER 1.2 — May 15, 2026 — getActorName helper
- FEATURE: getActorName(profile, user) — single authoritative function for
  resolving a human-readable actor name for system_activity_logs entries.
  Priority: profiles_v2 first/last name > user_metadata.full_name > email > fallback.
  Eliminates the scattered `user?.user_metadata?.full_name || user?.email || 'X'`
  pattern that caused emails to appear in the ticker when full_name was unset.
=========================================
VER 1.1 - Chronological Sorting Fix
- Date/Time: March 17, 2026
- BUG FIX: Upgraded `sortTickets` to process `scheduled_date` fields as absolute millisecond timestamps (`getTime()`) rather than strings. This prevents PostgreSQL formatting anomalies (like omitted leading zeros) from causing single-digit days to sort out of order alphabetically.
=========================================
*/
/* [ END OF MODULE - VER 2.3 (src/utils/ticketUtils.js) ] */